import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Route, useHistory, useLocation, useRouteMatch } from "react-router";

import { IconMagnifyingGlass } from "@btnet/blueprint-icons";
import { BTText } from "@btnet/core-components";
import { FeedbackTarget } from "@btnet/services-btnet/handlers/models/feedbackTarget";
import { cn } from "@btnet/utilities-core";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { EnvironmentInfoContext } from "helpers/globalContext/EnvironmentInfoContext";
import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import { useTracking } from "utilities/analytics/analytics";
import { routes } from "utilities/routes";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTIconLoadingOutlined, BTIconMagnifyingGlassX } from "commonComponents/btWrappers/BTIcon";
import FeedbackWidget from "commonComponents/feedback/FeedbackWidget/FeedbackWidget";
import { SearchBarRoutes } from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBarRoutes";

import "./AiSearch.less";

import { getSearchApiVersion } from "./aiSearchFlags";
import { AiSearchQA } from "./AiSearchQA";
import { AiSearchQASlot } from "./AiSearchQASlot";
import {
    AiSearchResults,
    getEntityLink,
    isVisibleHit,
    navigateEntityLink,
    OPTION_ID_PREFIX,
} from "./AiSearchResults";
import { legacyVisibility, stableListVisibility } from "./aiSearchVisibility";
import { buildClickEvent } from "./clickTelemetry";
import { DevContextSwitcher } from "./DevContextSwitcher";
import { getHitKey, normalizeKey } from "./entityMeta";
import { useAiSearch } from "./hooks/useAiSearch";
import { useAuthToken } from "./hooks/useAuthToken";
import { type IRapidAnswerSource, useRapidOverviewForSearch } from "./hooks/useRapidAnswer";
import { useSearchSuggest } from "./hooks/useSearchSuggest";
import type { AiSearchPhase, ICategorizedHit, ISearchHit } from "./types";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const SKELETON_COUNT = 4;
/**
 * Cap on the number of ranked results serialized into the "Search" telemetry
 * event. Keeps the payload well under Sawmill's per-event size limit while still
 * capturing the full visible result set for relevance analysis.
 */
const MAX_LOGGED_RESULTS = 50;
/**
 * PR-A2 (ADO 286302): fixed pixel height of the reserved QA answer slot. The
 * slot renders at this height the moment intent reports isQuestion (skeleton
 * card) and keeps it when the qa event replaces the skeleton with the answer
 * — so the arrival of the answer can never change the y-position of the
 * result list below it. Inline style, not a class, so the layout contract is
 * assertable in the DOM-level tests.
 */
const QA_SLOT_HEIGHT_PX = 132;
/**
 * A2-review (thread 1511891): answers longer than this line-clamp inside the
 * fixed slot and get a "Show full answer" toggle instead of clipping behind
 * an undiscoverable nested scrollbar. Expanding is a user-initiated layout
 * change (the slot grows to fit), which D4 permits — only PASSIVE arrivals
 * may never move painted content. ~3 clamp lines of answer text.
 */
const QA_CLAMP_THRESHOLD_CHARS = 220;

/**
 * graft/A7: the "Try asking" invitation in the empty dropdown. Universal
 * question shapes the AI overview answers on any builder's data - the point
 * is to show that a question is a valid thing to type here.
 */
const TRY_ASKING = [
    "Which bills are unpaid?",
    "What's on the schedule this week?",
    "Which purchase orders are still open?",
] as const;
/**
 * PR-A2: progress width floor. Before intent lands (no honest denominator —
 * see the progress block below) and for the 0-of-N instant after plans are
 * announced, the bar shows this sliver as the activity cue that replaced the
 * fake 1s loop animation.
 */
const PROGRESS_FLOOR_PCT = 5;
/**
 * graft/A5 (ADO 289471): DOM id prefix for typeahead suggestion options, so
 * the Tab-driven suggestion highlight is exposed through
 * aria-activedescendant the same way OPTION_ID_PREFIX exposes result rows.
 */
const SUGGEST_OPTION_ID_PREFIX = "ai-search-suggest-option-";

interface IAiSearchProps {
    handleHideSearchInput?: () => void;
    onFocus?: () => void;
    isSmallWidth: boolean;
    /**
     * Owned by the caller because `commonComponents` may not read builder flags
     * directly. Pass `useContext(BuilderInfoContext)` from the parent.
     */
    builderInfo?: BuilderInfo | null;
}

function SkeletonRows() {
    return (
        <div className="ai-search-skeleton-list" data-testid="ai-search-skeleton-list">
            {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <div className="ai-search-skeleton" key={i}>
                    <div
                        className="ai-search-skeleton-icon"
                        data-testid="ai-search-skeleton-icon"
                    />
                    <div className="ai-search-skeleton-lines">
                        <div
                            className="ai-search-skeleton-line ai-search-skeleton-line--medium"
                            data-testid="ai-search-skeleton-line"
                        />
                        <div
                            className="ai-search-skeleton-line ai-search-skeleton-line--short"
                            data-testid="ai-search-skeleton-line"
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * PR-A2: placeholder rendered inside the reserved QA slot between the intent
 * event (isQuestion) and the qa event. Same card chrome as the real answer so
 * the swap is a content change, not a layout change.
 */
function QASkeletonCard() {
    return (
        <div className="ai-search-qa" data-testid="ai-search-qa-skeleton">
            <div className="ai-search-qa-card">
                <div className="ai-search-qa-label">AI Answer</div>
                <div className="ai-search-skeleton-lines">
                    <div className="ai-search-skeleton-line ai-search-skeleton-line--medium" />
                    <div className="ai-search-skeleton-line ai-search-skeleton-line--short" />
                </div>
            </div>
        </div>
    );
}

export function AiSearch(props: IAiSearchProps) {
    const { handleHideSearchInput, onFocus, isSmallWidth, builderInfo } = props;
    const { doSearch, cancel, state, stableListEnabled } = useAiSearch(builderInfo);
    // ADO 289472: server-generation selection (AiPoweredSearchV1 builder
    // flag). "v1" pins this surface to origin/master behavior — main search
    // only, no graft sidecar calls (suggest, dev switcher); "v2" is the
    // graft experience. Derived from the same builderInfo the hook latches,
    // so the two can never disagree in production.
    const searchApiVersion = getSearchApiVersion(builderInfo);
    const { trackEvent } = useTracking({ component: "AI Search" });
    const history = useHistory();
    const match = useRouteMatch();
    const location = useLocation();
    const spaContext = useContext(SpaInfoContext);
    const appDefaultInfo = useContext(AppDefaultInfoContext);
    const environmentInfo = useContext(EnvironmentInfoContext);
    const suggestBaseUrl = appDefaultInfo?.searchServiceConfiguration?.apiBaseUrl ?? "";

    const [query, setQuery] = useState("");
    // graft/A5: typeahead + did-you-mean (dev-surface gated; inert when the
    // suggest endpoint is absent — i.e. every deployed environment today).
    const suggestAudience = appDefaultInfo?.searchServiceConfiguration?.authAudience ?? "";
    const { getToken: getSuggestToken } = useAuthToken(suggestAudience);
    const {
        suggestions,
        corrected,
        reset: resetSuggest,
        enabled: suggestEnabled,
    } = useSearchSuggest(suggestBaseUrl, query, getSuggestToken, searchApiVersion);

    // graft/A7 v2 (Rapid QA): the AI overview — only for an overview-shaped
    // query (a question, a quantity, a status set, a superlative, a dated
    // ask) and only at a stopping point: the input still for a beat, Enter
    // with nothing highlighted, a "Try asking" chip, a did-you-mean. Rides
    // the search's own searchId. Only the graft (v2) server has /v2/answer,
    // so a v1 builder gets an empty base url and the hook stays inert; a 404
    // from the server (flag off) does the same for the session.
    const rapid = useRapidOverviewForSearch(
        state,
        query,
        searchApiVersion === "v2" ? suggestBaseUrl : "",
        getSuggestToken,
    );
    const { commit: commitOverview, probe: probeOverview } = rapid;
    // A cited chip routes like a result click (getEntityLink + navigateEntityLink),
    // then the dropdown closes while the record modal/page mounts - the same
    // post-click contract as a result row.
    const openRapidSource = useCallback(
        (source: IRapidAnswerSource) => {
            const hit: ISearchHit = {
                entity: source.entity,
                id: source.id,
                score: 0,
                rerank_score: 0,
                reranker: "rapid-qa",
                payload: {
                    title: source.title,
                    ...(source.job_id ? { jobsite_id: source.job_id } : {}),
                },
            };
            const link = getEntityLink(hit, builderInfo ?? undefined);
            if (!link) return;
            navigateEntityLink(link, history, match.url, !!spaContext.isSpa);
            handleHideSearchInput?.();
        },
        [builderInfo, history, match.url, spaContext.isSpa, handleHideSearchInput],
    );
    const [panelOpen, setPanelOpen] = useState(isSmallWidth);
    // graft/A5 (ADO 289471): Tab/Shift+Tab keyboard highlight for the
    // typeahead suggestions. Deliberately SEPARATE from the result
    // highlightedIndex machinery below — suggestions are a short, transient
    // list keyed by position (no commits/appends/re-fuses), so index-only
    // state is enough and the key-relocation refs stay result-only.
    const [suggestHighlightedIndex, setSuggestHighlightedIndex] = useState(-1);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    // PR-A2 (ADO 286302): the KEY of the keyboard-highlighted hit. The index
    // is a position in a list that commits/appends/re-fuses under the cursor;
    // the key identifies the HIT, so the selection can follow it to its new
    // position instead of evaporating whenever the count changes.
    const highlightedKeyRef = useRef<string | null>(null);
    // A2-review (thread 1511894): the last COMMITTED navigable list, read
    // from inside the setHighlightedIndex functional updater instead of the
    // handler's closure. The `prev` index the updater receives is relative
    // to the list the user was looking at when they pressed the key (the
    // last committed render — the same commit whose DOM handler fired), and
    // this ref is synced in an effect on that same commit, so the index→hit
    // pairing stays consistent even if React flushes the updater against a
    // fresher closure (e.g. a result event landing between the keydown and
    // the flush). The relocation effect then carries the recorded key into
    // whatever list commits next.
    const navigableResultsRef = useRef<ICategorizedHit[]>([]);
    const [pendingKey, setPendingKey] = useState<string | null>(null);
    // PR-A2: user dismissed the per-op-error footer note for this search.
    const [opErrorsDismissed, setOpErrorsDismissed] = useState(false);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const resultsRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [dropdownLeft, setDropdownLeft] = useState<number | undefined>(undefined);
    const lastTrackedPhaseRef = useRef<string | null>(null);

    // Snapshot of the most recent search's outcome, kept in a ref so abandonment
    // can be reported from unmount/close/navigation handlers without stale closures.
    const searchOutcomeRef = useRef<{
        searchId: string;
        traceId: string | null;
        phase: AiSearchPhase;
        query: string;
        resultCount: number;
        clicked: boolean;
    }>({ searchId: "", traceId: null, phase: "idle", query: "", resultCount: 0, clicked: false });
    // searchIds already reported as abandoned, so close + unmount + next-search
    // can't emit duplicate abandonment events for the same search.
    const abandonReportedRef = useRef<Set<string>>(new Set());

    // Gap #4 (no-match floor, BTNet-Search 83a8ce6): when the server's
    // committed event flagged the ranking as semantic-only fallback noise
    // (no_strong_match), the rows stay in state for telemetry but the VIEW
    // suppresses them — the user gets the empty state (plus the suggest
    // layer's did-you-mean banner, which renders independently) instead of
    // 50 recency-clustered guesses.
    const results = useMemo(
        () => (state.noStrongMatch ? [] : state.results.filter(isVisibleHit)),
        [state.results, state.noStrongMatch],
    );
    const staleResults = useMemo(
        () => state.staleResults.filter(isVisibleHit),
        [state.staleResults],
    );
    // PR-A1 (stable list): late-op hits rendered below the committed list.
    // Same view-level visibility filter as the committed rows.
    const appendedResults = useMemo(
        () => state.appendedResults.filter(isVisibleHit),
        [state.appendedResults],
    );
    // The FULL visible list, committed rows first then appended rows: keyboard
    // navigation walks it as one listbox, and telemetry reports it as the
    // ranking the user actually saw. Identical to `results` when the stable
    // list flag is off (appendedResults is always empty on the legacy path).
    const navigableResults = useMemo(
        () => (appendedResults.length > 0 ? [...results, ...appendedResults] : results),
        [results, appendedResults],
    );

    const hasQuery = query.trim().length >= MIN_QUERY_LENGTH;
    const hasResults = results.length > 0;
    const hasStaleResults = staleResults.length > 0;
    const hasQA = Object.keys(state.qaAnswers).length > 0;
    const showPanel = panelOpen;
    const showInstruction = panelOpen && !hasQuery && state.phase === "idle";
    // graft/A7 v2: opening the panel is the moment to ask whether the
    // overview is on at all (server flag, probed once per page load); the
    // "Try asking" chips and the "…or ask a question" copy show only then.
    useEffect(() => {
        if (panelOpen && searchApiVersion === "v2") probeOverview();
    }, [panelOpen, searchApiVersion, probeOverview]);
    // resultCount spans both sections: keyboard nav walks committed rows then
    // appended rows, and telemetry reports the full visible count.
    const resultCount = results.length + appendedResults.length;
    // Each rendering contract's visibility rules live in their own pure
    // function (aiSearchVisibility.ts) — the flag is fixed per mount, so the
    // contracts never mix within a render (review 1511770).
    const { showSkeleton, showStaleResults, showResults, showAppended, showEmpty } =
        stableListEnabled
            ? stableListVisibility({
                  hasQuery,
                  phase: state.phase,
                  committed: state.committed,
                  resultCount,
                  hasResults,
                  appendedCount: appendedResults.length,
              })
            : legacyVisibility({
                  hasQuery,
                  phase: state.phase,
                  hasResults,
                  hasStaleResults,
              });

    // ── PR-A2: reserved QA slot (composes WITH aiSearchVisibility, which
    // owns the LIST contract; the slot is driven by intent/qa events and is
    // identical in both flag states, so it lives here rather than in either
    // contract function). The slot renders the moment intent reports
    // isQuestion — a fixed-height skeleton card the eventual qa event
    // replaces IN PLACE, so the answer's arrival can never shove the result
    // list (the qa-before-done layout churn).
    //
    // A2-review (thread 1511890): once reserved, the slot holds its height
    // for the REST OF THE SEARCH — collapsing at an answerless `done` would
    // yank the list UP 132px mid-read, the same shove relocated to stream
    // end. An answerless end swaps the shimmer for a quiet "no answer" state
    // in the same slot. The slot leaves only with the whole view: new search
    // (doSearch resets isQuestion), clear/cancel (phase idle), or close.
    const qaStreamOpen = state.phase === "loading" || state.phase === "streaming";
    // graft/A7 v2: the overview owns the slot when the LIVE input is
    // overview-shaped (the frame is reserved before any request) or when its
    // stream is active for THIS search (matched on searchId so a superseded
    // search can never own it). A lookup owes no slot at all.
    const rapidActive =
        hasQuery &&
        (rapid.armed || (rapid.state.phase !== "idle" && rapid.state.searchId === state.searchId));
    const showQASlot = hasQA || (state.isQuestion && hasQuery && state.phase !== "idle");
    const qaSlotContent: "answer" | "skeleton" | "no-answer" = hasQA
        ? "answer"
        : qaStreamOpen
          ? "skeleton"
          : "no-answer";
    // A2-review (thread 1511891): long answers line-clamp inside the fixed
    // slot with a "Show full answer" toggle instead of hiding overflow behind
    // a nested scrollbar. Expansion is user-initiated (D4 allows it); it
    // resets per search.
    const [qaExpanded, setQaExpanded] = useState(false);
    const qaNeedsClamp = Object.values(state.qaAnswers).some(
        (a) => a.text.length > QA_CLAMP_THRESHOLD_CHARS,
    );
    const qaSlotExpanded = qaExpanded && qaNeedsClamp;

    // Emit an "abandoned" search event when a completed search (results shown or
    // empty) is left without the user clicking a result. Guarded so the same
    // searchId is reported at most once across the close/unmount/next-search paths.
    const emitAbandon = useCallback(
        (snap: typeof searchOutcomeRef.current) => {
            if (!snap.searchId || snap.clicked) return;
            if (snap.phase !== "done" && snap.phase !== "empty") return;
            if (abandonReportedRef.current.has(snap.searchId)) return;
            abandonReportedRef.current.add(snap.searchId);
            trackEvent({
                event: "Search",
                extraInfo: {
                    searchId: snap.searchId,
                    // Same null-safe trace correlation as click events, so an
                    // abandoned search joins to its server span the same way
                    // a clicked one does (abandonment is the negative signal).
                    traceId: snap.traceId,
                    query: snap.query,
                    resultCount: snap.resultCount,
                    searchPhase: "abandoned",
                },
            });
        },
        [trackEvent],
    );

    // Hold the latest emitAbandon so the unmount-only cleanup can call it without
    // depending on its identity. Listing emitAbandon as a dependency there would
    // make the cleanup fire whenever its identity changes (e.g. if trackEvent is
    // recreated), emitting a spurious abandonment while the user is still viewing
    // results.
    const emitAbandonRef = useRef(emitAbandon);
    useEffect(() => {
        emitAbandonRef.current = emitAbandon;
    }, [emitAbandon]);

    // Keep searchOutcomeRef current, and report abandonment when one search is
    // superseded by another (query reformulation) without an intervening click.
    // Uses state.searchQuery (the query that triggered the search), not the live
    // input, so a deferred abandonment event reports the original query rather
    // than whatever the user has since typed.
    useEffect(() => {
        const prev = searchOutcomeRef.current;
        if (prev.searchId && prev.searchId !== state.searchId) {
            emitAbandon(prev);
        }
        searchOutcomeRef.current = {
            searchId: state.searchId,
            traceId: state.traceId,
            phase: state.phase,
            query: state.searchQuery,
            resultCount,
            clicked: prev.searchId === state.searchId ? prev.clicked : false,
        };
    }, [state.searchId, state.traceId, state.phase, state.searchQuery, resultCount, emitAbandon]);

    const handleInputChange = useCallback(
        (value: string) => {
            setQuery(value);
            setPanelOpen(true);
            setHighlightedIndex(-1);
            highlightedKeyRef.current = null;
            // Cancel any pending post-click close — otherwise the 150ms timer
            // from handleResultClick wipes the user's newly-typed query.
            setPendingKey(null);
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
            }

            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            if (value.trim().length < MIN_QUERY_LENGTH) {
                // Clearing/shortening below the threshold abandons the current
                // completed search just like a close or navigation — emit before
                // cancel() flips phase to "idle" and disqualifies the snapshot.
                // emitAbandon's done/empty guard ignores in-flight cancellations
                // and the dedup set prevents a double-emit if a close/nav follows.
                emitAbandon(searchOutcomeRef.current);
                cancel();
                return;
            }

            debounceRef.current = setTimeout(() => {
                doSearch(value.trim());
            }, DEBOUNCE_MS);
        },
        [doSearch, cancel, emitAbandon],
    );

    const handleClose = useCallback(() => {
        emitAbandon(searchOutcomeRef.current);
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setPanelOpen(false);
        setQuery("");
        setHighlightedIndex(-1);
        highlightedKeyRef.current = null;
        setPendingKey(null);
        cancel();
        handleHideSearchInput?.();
    }, [cancel, handleHideSearchInput, emitAbandon]);

    // Clear query/panel state on route navigation so input content doesn't
    // leak forward across back/forward or sidebar nav. Skip our own /SearchResult/
    // pushes (triggered by handleResultClick → navigateEntityLink).
    const previousPathnameRef = useRef(location.pathname);
    useEffect(() => {
        if (location.pathname === previousPathnameRef.current) return;
        const isSelfPushToModal = location.pathname.startsWith(
            previousPathnameRef.current.replace(/\/$/, "") + routes.search.resultModalPrefix + "/",
        );
        previousPathnameRef.current = location.pathname;
        if (isSelfPushToModal) return;
        // Navigating elsewhere (sidebar, back/forward) without clicking a result
        // is an abandonment of the current search.
        emitAbandon(searchOutcomeRef.current);
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setQuery("");
        setHighlightedIndex(-1);
        highlightedKeyRef.current = null;
        setPendingKey(null);
        setPanelOpen(isSmallWidth);
        cancel();
    }, [location.pathname, isSmallWidth, cancel, emitAbandon]);

    const handleFocus = useCallback(() => {
        setPanelOpen(true);
        onFocus?.();
    }, [onFocus]);

    const trackSearch = useCallback(
        (searchPhase: "first-results" | "complete" | "interrupted") => {
            const durationMs =
                state.searchStartTime != null ? Date.now() - state.searchStartTime : undefined;
            trackEvent({
                event: "Search",
                extraInfo: {
                    searchId: state.searchId,
                    // Null-safe server-span correlation, same as click events
                    // (null when the trace SSE event hasn't arrived yet).
                    traceId: state.traceId,
                    // The query that triggered this search, not the live input —
                    // consistent with the abandonment path, so a "complete" event
                    // can't log a newly-typed query against the old search's id.
                    query: state.searchQuery,
                    resultCount,
                    // Total hits returned before client-side filtering (excluded
                    // entities + unroutable hits), spanning committed AND appended
                    // sections so raw >= visible always holds (review 1511772).
                    // resultCount is the visible count.
                    rawResultCount: state.results.length + state.appendedResults.length,
                    searchPhase,
                    durationMs,
                    // Parsed intent (entity targeting + question detection).
                    entityScope: state.entityScope,
                    scopedEntities: [...state.scopedEntities],
                    isQuestion: state.isQuestion,
                    // The full ranked result list the user is shown (committed +
                    // appended, in visible order). Titles are intentionally
                    // omitted — summaries can contain customer PII; ids + scores
                    // are enough to reconstruct ranking quality.
                    results: navigableResults.slice(0, MAX_LOGGED_RESULTS).map((hit, i) => ({
                        rank: i + 1,
                        entity: normalizeKey(hit.entity),
                        id: hit.id,
                        score: hit.score,
                        rerankScore: hit.rerank_score,
                        reranker: hit.reranker,
                        modalities: [...new Set(hit.sourceOps.map((op) => op.modality))],
                    })),
                    // Per-operation breakdown: which sub-queries ran, their modality,
                    // how many hits each returned, and how long each took.
                    operations: state.operations.map((op) => ({
                        id: op.id,
                        entity: op.entity,
                        modality: op.modality,
                        stage: op.stage,
                        status: op.status,
                        resultCount: op.resultCount,
                        elapsedMs: op.elapsed_ms,
                    })),
                    qaAnswerSources: Object.keys(state.qaAnswers),
                },
            });
        },
        [
            trackEvent,
            resultCount,
            navigableResults,
            state.searchId,
            state.traceId,
            state.searchQuery,
            state.searchStartTime,
            state.results.length,
            state.appendedResults.length,
            state.entityScope,
            state.scopedEntities,
            state.isQuestion,
            state.operations,
            state.qaAnswers,
        ],
    );

    // lastTrackedPhaseRef prevents duplicate events when trackSearch changes
    useEffect(() => {
        if (state.phase === "streaming" && lastTrackedPhaseRef.current !== "streaming") {
            lastTrackedPhaseRef.current = "streaming";
            trackSearch("first-results");
        } else if (
            (state.phase === "done" || state.phase === "empty") &&
            lastTrackedPhaseRef.current !== state.phase
        ) {
            lastTrackedPhaseRef.current = state.phase;
            trackSearch("complete");
        } else if (state.phase === "loading" || state.phase === "idle") {
            lastTrackedPhaseRef.current = null;
        }
    }, [state.phase, trackSearch]);

    const handleResultClick = useCallback(
        (hit: ICategorizedHit, index: number, navMethod: "click" | "keyboard" = "click") => {
            if (state.phase !== "done" && state.phase !== "empty") {
                trackSearch("interrupted");
            }
            // Mark this search as having a click so it isn't later counted as abandoned.
            searchOutcomeRef.current.clicked = true;

            // Attribute the click to the search whose results the user was
            // actually shown. A click on a faded stale row (previous search's
            // results displayed while a new search loads) belongs to the
            // PREVIOUS search — its id, trace, query, and shown-list — not to
            // the one currently in flight. Staleness is derived from which
            // list the clicked row is actually rendered from (identity check
            // against the memoized stale list) rather than from the call
            // site, so every entry path — mouse, keyboard, future ones —
            // attributes correctly by construction.
            const isStale = state.staleSearchContext !== null && staleResults[index] === hit;
            const stale = isStale ? state.staleSearchContext : null;
            // The shown list is what the user actually sees, top to bottom:
            // committed rows then appended rows (navigableResults) — the SAME
            // concatenation AiSearchResults renders and keyboard nav walks,
            // and the same order `index` addresses (AiSearchResults passes the
            // overall index for appended rows). On the legacy path
            // appendedResults is always empty, so this equals `results`.
            const visible = isStale ? staleResults : navigableResults;
            // Contract-valid result-click payload (contracts/click-event.schema.json,
            // canonical: BTNet-Search docs/contracts/click-event.schema.json).
            // The server-side click-join turns it into training triples, keyed
            // by trace_id when the trace event arrived, else by search_id.
            // Emitted EVEN when the trace never arrived (trace_id null) and
            // after aborts — the shown results remain clickable either way.
            // Null only when the resolved search_id fails the server
            // sanitizer's pattern (an unjoinable event is more useful absent
            // than malformed); the legacy LinkClick fields still fire.
            const clickEvent = buildClickEvent({
                searchId: stale?.searchId ?? state.canonicalSearchId ?? state.searchId,
                traceId: isStale ? (stale?.traceId ?? null) : state.traceId,
                hit,
                index,
                visible,
                query: stale?.query ?? state.searchQuery,
                builderId: builderInfo?.builderId,
            });

            trackEvent({
                event: "LinkClick",
                element: "Link",
                uniqueId: `${normalizeKey(hit.entity)}`,
                extraInfo: {
                    searchId: state.searchId,
                    searchQuery: query,
                    searchResultRank: index + 1,
                    searchResultId: hit.id,
                    searchResultTitle: hit.summary,
                    // Legacy 278622 field — folding here is intentional and
                    // stays. Only clickEvent.entity must remain verbatim
                    // (the join matches it with plain equality).
                    searchResultType: normalizeKey(hit.entity),
                    searchResultScore: hit.score,
                    searchResultRerankScore: hit.rerank_score,
                    resultCount,
                    timeToClickMs:
                        state.searchStartTime != null
                            ? Date.now() - state.searchStartTime
                            : undefined,
                    navMethod,
                    ...(clickEvent !== null && { clickEvent }),
                },
            });

            // Show a brief pending acknowledgement on the clicked row so the
            // user sees the click registered even though the entity modal takes
            // 1-2s to mount.
            setPendingKey(getHitKey(hit));
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = setTimeout(() => {
                closeTimeoutRef.current = null;
                handleClose();
            }, 150);
        },
        [
            trackSearch,
            trackEvent,
            query,
            handleClose,
            navigableResults,
            staleResults,
            builderInfo,
            state.phase,
            state.searchId,
            state.searchQuery,
            state.searchStartTime,
            state.traceId,
            state.canonicalSearchId,
            state.staleSearchContext,
            resultCount,
        ],
    );

    // Focus input when panel opens at small widths (input transitions from hidden to visible)
    useEffect(() => {
        if (panelOpen && isSmallWidth && inputRef.current) {
            inputRef.current.focus();
        }
    }, [panelOpen, isSmallWidth]);

    // Left-align dropdown with input at wide widths, updating on resize
    useEffect(() => {
        if (!panelOpen || isSmallWidth || !inputRef.current) {
            setDropdownLeft(undefined);
            return;
        }

        const input = inputRef.current;
        const sync = () => setDropdownLeft(input.getBoundingClientRect().left);
        sync();

        const ro = new ResizeObserver(sync);
        ro.observe(document.body);
        return () => ro.disconnect();
    }, [panelOpen, isSmallWidth]);

    // Navigate to highlighted result on Enter
    const navigateToHighlighted = useCallback(() => {
        if (highlightedIndex >= 0 && highlightedIndex < navigableResults.length) {
            const hit = navigableResults[highlightedIndex];
            const link = getEntityLink(hit, builderInfo ?? undefined);
            if (!link) return;

            navigateEntityLink(link, history, match.url, !!spaContext.isSpa);
            handleResultClick(hit, highlightedIndex, "keyboard");
        }
    }, [
        highlightedIndex,
        navigableResults,
        handleResultClick,
        history,
        match.url,
        spaContext.isSpa,
        builderInfo,
    ]);

    // graft/A5 (ADO 289471): applying a suggestion — shared verbatim by the
    // suggestion row's onClick and the keyboard Enter path, so the two entry
    // points can never drift.
    const applySuggestion = useCallback(
        (text: string) => {
            setQuery(text);
            resetSuggest();
            doSearch(text);
            // graft/A7 v2: a click is a stopping point — the overview (if the
            // query earns one) starts with the search, no settle wait.
            commitOverview(text);
        },
        [resetSuggest, doSearch, commitOverview],
    );

    // Keyboard navigation. PR-A2: every arrow move records the highlighted
    // hit's KEY alongside the index — the key is what lets the selection
    // survive commits/appends/re-fuses that change the count or move the row.
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            const count = navigableResults.length;
            // Functional updater (rapid keydowns before a re-render see the
            // real previous index); recording the key in a ref is idempotent,
            // so the updater stays safe under double invocation. The list is
            // read through navigableResultsRef — synced to the committed
            // render — not this closure, so the flush-time index can never be
            // paired with a mismatched array (A2 review thread 1511894).
            const move = (step: (prev: number, listCount: number) => number) => {
                setHighlightedIndex((prev) => {
                    const list = navigableResultsRef.current;
                    if (list.length === 0) return prev;
                    const next = Math.min(Math.max(step(prev, list.length), 0), list.length - 1);
                    highlightedKeyRef.current = getHitKey(list[next]);
                    return next;
                });
            };

            switch (e.key) {
                case "ArrowDown": {
                    e.preventDefault();
                    if (count === 0) return;
                    // Wrap bounds come from the ref inside `move`, so a list
                    // that changed size between keydown and flush cannot
                    // produce an out-of-range index.
                    move((prev, listCount) => (prev < listCount - 1 ? prev + 1 : 0));
                    break;
                }
                case "ArrowUp": {
                    e.preventDefault();
                    if (count === 0) return;
                    move((prev, listCount) => (prev > 0 ? prev - 1 : listCount - 1));
                    break;
                }
                // graft/A5 (ADO 289471): Tab / Shift+Tab walk the typeahead
                // suggestions (wrapping). Only intercepted while suggestions
                // are on screen — with none, Tab falls through untouched so
                // focus behavior elsewhere is unchanged.
                case "Tab": {
                    if (suggestions.length === 0) return;
                    e.preventDefault();
                    setSuggestHighlightedIndex((prev) =>
                        e.shiftKey
                            ? prev <= 0
                                ? suggestions.length - 1
                                : prev - 1
                            : prev >= suggestions.length - 1
                              ? 0
                              : prev + 1,
                    );
                    break;
                }
                case "Enter": {
                    // graft/A5 (ADO 289471): a Tab-highlighted suggestion wins
                    // over the result highlight — same behavior as clicking
                    // the suggestion row.
                    if (
                        suggestHighlightedIndex >= 0 &&
                        suggestHighlightedIndex < suggestions.length
                    ) {
                        e.preventDefault();
                        applySuggestion(suggestions[suggestHighlightedIndex].text);
                        break;
                    }
                    if (highlightedIndex >= 0 && highlightedIndex < count) {
                        e.preventDefault();
                        navigateToHighlighted();
                        break;
                    }
                    // graft/A7 v2: Enter with nothing highlighted is the
                    // stopping point for the AI overview. Run the search now
                    // if the debounce still holds it, then commit — a no-op
                    // for a lookup and for an overview already started.
                    if (hasQuery) {
                        e.preventDefault();
                        if (debounceRef.current) {
                            clearTimeout(debounceRef.current);
                            debounceRef.current = null;
                        }
                        const typed = query.trim();
                        if (state.searchQuery !== typed) doSearch(typed);
                        commitOverview(typed);
                    }
                    break;
                }
                case "Escape": {
                    e.preventDefault();
                    handleClose();
                    break;
                }
            }
        },
        [
            navigableResults.length,
            highlightedIndex,
            navigateToHighlighted,
            handleClose,
            suggestions,
            suggestHighlightedIndex,
            applySuggestion,
            hasQuery,
            query,
            state.searchQuery,
            doSearch,
            commitOverview,
        ],
    );

    // Scroll highlighted result into view
    useEffect(() => {
        if (highlightedIndex < 0) return;
        const el = document.getElementById(`${OPTION_ID_PREFIX}${highlightedIndex}`);
        if (el) {
            el.scrollIntoView({ block: "nearest" });
        }
    }, [highlightedIndex]);

    // graft/A5 (ADO 289471): the suggestion highlight is positional, so it
    // resets whenever the suggestions array IDENTITY changes — a new suggest
    // response (new keystrokes), resetSuggest (identity flips to []), or a
    // new search clearing the layer. Mirrors the result-highlight reset
    // below, minus the key relocation (suggestion lists don't re-fuse).
    useEffect(() => {
        setSuggestHighlightedIndex(-1);
    }, [suggestions, state.searchId]);

    // Reset the keyboard highlight when the list's IDENTITY changes (A1
    // review 1511769), in BOTH flag states: a new search (searchId) and the
    // stable path's one-time commit (committed false→true, when the buffered
    // rows replace whatever the index pointed at; always false flag-off).
    // PR-A2 merge: the recorded KEY clears in lockstep with the index so the
    // relocation effect below can't resurrect a previous search's selection
    // when the new stream happens to contain the same hit.
    useEffect(() => {
        highlightedKeyRef.current = null;
        setHighlightedIndex(-1);
    }, [state.searchId, state.committed]);

    // PR-A2 (ADO 286302, both flag states): when the visible list changes
    // WITHIN a search, RELOCATE the keyboard selection by hit key instead of
    // wiping it. This replaces the pre-A2 legacy reset-on-resultCount effect
    // (flipped deliberately with its T4 baselines): the cursor follows the
    // hit it was on — to its new index if the row moved (legacy per-event
    // re-fuses), unchanged if the row didn't (stable-path appends), and
    // cleared only when the highlighted hit itself left the list. Identity
    // changes (new search / commit) are handled by the reset above, which
    // runs first in declaration order.
    useEffect(() => {
        // Sync the committed-list ref FIRST so a keydown arriving after this
        // render pairs its index with this exact list (thread 1511894).
        navigableResultsRef.current = navigableResults;
        const key = highlightedKeyRef.current;
        if (key === null) return;
        const nextIndex = navigableResults.findIndex((hit) => getHitKey(hit) === key);
        if (nextIndex === -1) {
            // The highlighted hit left the list: clear key and index in
            // LOCKSTEP so the next ArrowDown starts from the top instead of
            // resuming from a stale index into a different row.
            highlightedKeyRef.current = null;
            setHighlightedIndex(-1);
        } else {
            // No-op setState when the index is unchanged: React bails out.
            setHighlightedIndex(nextIndex);
        }
    }, [navigableResults]);

    // Close on outside click
    useEffect(() => {
        if (!panelOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (isFeedbackOpen) return;
            const target = e.target as Node;
            if (
                panelRef.current &&
                !panelRef.current.contains(target) &&
                inputRef.current &&
                !inputRef.current.contains(target)
            ) {
                handleClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [panelOpen, handleClose, isFeedbackOpen]);

    // Emit a final abandonment and abort in-flight requests on unmount ONLY.
    // Empty deps + reading emitAbandon through a ref keep this from firing
    // mid-session when a dependency's identity changes. cancel is stable
    // (useCallback with empty deps in the hook).
    useEffect(() => {
        return () => {
            emitAbandonRef.current(searchOutcomeRef.current);
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
            cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // graft/A5 (ADO 289471): a Tab-highlighted suggestion takes the
    // active-descendant slot (it is also what Enter would apply); otherwise
    // the arrow-highlighted result keeps it, as before.
    const activeDescendant =
        suggestHighlightedIndex >= 0
            ? `${SUGGEST_OPTION_ID_PREFIX}${suggestHighlightedIndex}`
            : highlightedIndex >= 0
              ? `${OPTION_ID_PREFIX}${highlightedIndex}`
              : undefined;

    const resultCountText = resultCount === 1 ? "1 result" : `${resultCount} results`;

    // ── PR-A2: real progress from the per-op bookkeeping ─────────────────
    // settled/total over state.operations replaces the fake 1s loop
    // animation. "Settled" includes errored ops: a failed op is finished
    // work, and a bar that waits on it would never reach its end.
    //
    // A2-review (thread 1511889): the bar is INDETERMINATE (floor width, no
    // aria values) until the intent event lands. Default ops are only
    // knowable from their own result events, so before intent every op
    // enters the array already settled — numerator and denominator increment
    // together and the bar pins to a dishonest 100%, then jumps BACKWARDS
    // when intent announces plans. intent is the first moment an honest
    // denominator exists; after it, a newly discovered op arrives settled,
    // so the fraction is monotonic non-decreasing.
    const opsTotal = state.operations.length;
    const opsSettled = state.operations.filter(
        (op) => op.status === "complete" || op.status === "error",
    ).length;
    const progressDeterminate = state.intentReceived && opsTotal > 0;
    const progressPct = progressDeterminate
        ? Math.max(PROGRESS_FLOOR_PCT, Math.round((opsSettled / opsTotal) * 100))
        : PROGRESS_FLOOR_PCT;

    // ── PR-A2: screen-reader result-count announcement ───────────────────
    // One visually-hidden live region announces the count once per commit
    // (flag-on) or once per visible paint (flag-off) — never per SSE event.
    // The old header aria-live re-announced on every count change AND when
    // the "so far..." suffix toggled at done; the visual header keeps its
    // dynamic text but is no longer a live region.
    const [announcement, setAnnouncement] = useState("");
    // Guards the flag-on once-per-commit contract: appended results change
    // resultCount after the commit but must not re-announce (they render
    // below the fold and must not interrupt the user's screen reader).
    const announcedSearchIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (resultCount === 0) return; // nothing visible; empty state has its own live region
        if (stableListEnabled) {
            if (!state.committed) return; // skeletons up — nothing painted yet
            if (announcedSearchIdRef.current === state.searchId) return;
            announcedSearchIdRef.current = state.searchId;
        }
        setAnnouncement(resultCount === 1 ? "1 result" : `${resultCount} results`);
    }, [resultCount, stableListEnabled, state.committed, state.searchId]);

    // PR-A2: per-search resets — the footer-note dismissal, the QA expansion
    // (review 1511891), and the live region (cleared so a repeat search
    // announcing the SAME count still fires: aria-live only speaks on text
    // CHANGE).
    useEffect(() => {
        setOpErrorsDismissed(false);
        setQaExpanded(false);
        setAnnouncement("");
    }, [state.searchId]);
    const showOpErrorsNote =
        hasQuery &&
        !state.error &&
        state.opErrors.length > 0 &&
        !opErrorsDismissed &&
        state.phase !== "idle";

    const feedbackExtraInfo = JSON.stringify({
        searchId: state.searchId,
        query,
        resultCount,
        topResults: navigableResults.slice(0, 10).map((r) => ({
            entity: r.entity,
            id: r.id,
            summary: r.summary,
        })),
    });

    return (
        <div className="ai-search-wrapper">
            <div className="ai-search-input-row">
                {/* Native input required: BTInput doesn't support combobox
                    role, aria-activedescendant, or aria-expanded */}
                {/* eslint-disable-next-line react/forbid-elements */}
                <input
                    ref={inputRef}
                    className="ai-search-overlay-input"
                    type="text"
                    placeholder={isSmallWidth ? "Search..." : "Search or ask a question..."}
                    value={query}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    role="combobox"
                    aria-expanded={hasQuery}
                    aria-controls="ai-search-listbox"
                    aria-activedescendant={activeDescendant}
                    aria-autocomplete="list"
                    data-testid="ai-search-input"
                />
            </div>

            {showPanel && (
                <>
                    <div
                        className={cn("ai-search-backdrop", {
                            "ai-search-backdrop--behind-modal": isFeedbackOpen,
                        })}
                        onClick={() => {
                            if (!isFeedbackOpen) handleClose();
                        }}
                        data-testid="ai-search-overlay"
                    />
                    <div
                        ref={panelRef}
                        className={cn("ai-search-dropdown", {
                            "ai-search-dropdown--mobile": isSmallWidth,
                            "ai-search-dropdown--behind-modal": isFeedbackOpen,
                        })}
                        style={
                            !isSmallWidth && dropdownLeft !== undefined
                                ? { left: dropdownLeft }
                                : undefined
                        }
                        data-testid="ai-search-panel"
                    >
                        {/* graft/A5: dev-only builder/user switcher (probe-gated).
                            ADO 289472: graft-only surface — never mounted (and
                            its dev-surface probes never fire) on the v1 path. */}
                        {searchApiVersion === "v2" && (
                            <DevContextSwitcher
                                searchBaseUrl={suggestBaseUrl}
                                isProduction={environmentInfo?.isProduction ?? true}
                                onChanged={() => {
                                    if (query.trim()) doSearch(query.trim());
                                }}
                            />
                        )}

                        {/* PR-A2: progress bar driven by the real per-op
                            bookkeeping (settled/total operations) instead of
                            the fake 1s loop animation; indeterminate until
                            intent provides an honest denominator. */}
                        {(state.phase === "loading" || state.phase === "streaming") && (
                            <div
                                className="ai-search-progress"
                                role="progressbar"
                                aria-label="Search progress"
                                aria-valuemin={0}
                                aria-valuemax={progressDeterminate ? opsTotal : undefined}
                                aria-valuenow={progressDeterminate ? opsSettled : undefined}
                                data-testid="ai-search-progress"
                            >
                                <div
                                    className="ai-search-progress-bar ai-search-progress-bar--ops"
                                    style={{ width: `${progressPct}%` }}
                                    data-testid="ai-search-progress-bar"
                                />
                            </div>
                        )}

                        {/* Instruction: no query yet */}
                        {showInstruction && (
                            <div
                                className="ai-search-instruction"
                                data-testid="ai-search-instruction"
                            >
                                <div className="ai-search-instruction-icon">
                                    <IconMagnifyingGlass />
                                </div>
                                <h3>Find anything in Buildertrend</h3>
                                <p>
                                    Search across all your jobs
                                    {rapid.available && searchApiVersion === "v2"
                                        ? ", or ask a question."
                                        : "."}
                                </p>
                                {/* graft/A7: the invitation - three universal question
                                    shapes the AI overview answers on any builder. Hidden
                                    on v1 builders and when the server has no Rapid QA. */}
                                {rapid.available && searchApiVersion === "v2" && (
                                    <div
                                        className="ai-search-instruction-try"
                                        data-testid="ai-search-try"
                                    >
                                        {TRY_ASKING.map((q) => (
                                            <BTButton
                                                key={q}
                                                type="tertiary"
                                                className="ai-search-instruction-try-chip"
                                                data-testid="ai-search-try-chip"
                                                onClick={() => applySuggestion(q)}
                                            >
                                                {q}
                                            </BTButton>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Skeleton loading (first search only, no prior results) */}
                        {showSkeleton && <SkeletonRows />}

                        {/* Stale results — faded previous results while new search loads */}
                        {showStaleResults && (
                            <div
                                className="ai-search-results-scroll ai-search-results-scroll--stale"
                                data-testid="ai-search-stale-results"
                            >
                                <AiSearchResults
                                    results={staleResults}
                                    query={query}
                                    highlightedIndex={-1}
                                    pendingKey={pendingKey}
                                    onResultClick={handleResultClick}
                                    builderInfo={builderInfo}
                                />
                            </div>
                        )}

                        {/* Fatal error banner (auth/transport/all-ops-failed).
                            PR-A2: per-op errors no longer render here — they
                            demote to the dismissible footer note below. */}
                        {state.error && (
                            <div className="ai-search-error" data-testid="ai-search-error">
                                {state.error}
                            </div>
                        )}

                        {/* graft/A5: did-you-mean (spell banner) */}
                        {corrected && (
                            <div
                                className="ai-search-dym"
                                data-testid="ai-search-dym"
                                style={{
                                    padding: "6px 16px",
                                    fontSize: 13,
                                    background: "#fff8e6",
                                    borderBottom: "1px solid #f0e2b6",
                                }}
                            >
                                Did you mean{" "}
                                <button
                                    type="button"
                                    data-testid="ai-search-dym-apply"
                                    onClick={() => {
                                        setQuery(corrected);
                                        resetSuggest();
                                        doSearch(corrected);
                                        commitOverview(corrected);
                                    }}
                                    style={{
                                        border: "none",
                                        background: "none",
                                        color: "#8a6d1a",
                                        fontWeight: 700,
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        padding: 0,
                                        fontSize: 13,
                                    }}
                                >
                                    {corrected}
                                </button>
                                ?
                            </div>
                        )}

                        {/* graft/A5: typeahead suggestions (authed suggest route) */}
                        {/* graft/A5: v3 AgentTriageBanner — invite the agent
                            once a query has committed. Quiet by default,
                            nudges on question-shaped queries. Probe-gated:
                            suggestEnabled goes false wherever the authed
                            suggest route is absent (404 or SPA-HTML), so
                            this renders nowhere the dock doesn't. */}
                        {suggestEnabled && state.searchQuery.trim().length > 0 && (
                            <div
                                data-testid="ai-search-agent-banner"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "6px 16px",
                                    fontSize: 13,
                                    background:
                                        /^(who|what|where|when|why|how|which|is|are|does|do|can|should)/i.test(
                                            state.searchQuery,
                                        ) || state.searchQuery.includes("?")
                                            ? "#f3efff"
                                            : "#f7f9fb",
                                    borderBottom: "1px solid #e3e8ee",
                                    color: "#4a5560",
                                }}
                            >
                                <span
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    Want more than a list? The search agent can dig in, total, and
                                    follow up.
                                </span>
                                <button
                                    type="button"
                                    data-testid="ai-search-agent-banner-open"
                                    onClick={() => {
                                        window.dispatchEvent(
                                            new CustomEvent("bt:openSearchAgent", {
                                                detail: { query: state.searchQuery },
                                            }),
                                        );
                                        handleClose();
                                    }}
                                    style={{
                                        flexShrink: 0,
                                        padding: "3px 10px",
                                        borderRadius: 5,
                                        border: "none",
                                        background: "#1f4f6f",
                                        color: "#fff",
                                        fontSize: 12,
                                        cursor: "pointer",
                                    }}
                                >
                                    Ask the agent →
                                </button>
                            </div>
                        )}

                        {/* graft/A5 (ADO 289471): Tab/Shift+Tab move the
                            highlight through these rows (wrapping) and Enter
                            applies the highlighted one — same path as a
                            click. Own listbox, separate from the result
                            listbox: the suggestion highlight never touches
                            the result highlight machinery. */}
                        {suggestions.length > 0 && (
                            <div
                                className="ai-search-suggest"
                                data-testid="ai-search-suggest"
                                role="listbox"
                                aria-label="Search suggestions"
                            >
                                {suggestions.map((sg, i) => (
                                    <button
                                        key={`${sg.entity_type}-${sg.id}-${i}`}
                                        type="button"
                                        data-testid="ai-search-suggest-item"
                                        role="option"
                                        aria-selected={i === suggestHighlightedIndex}
                                        id={
                                            i === suggestHighlightedIndex
                                                ? `${SUGGEST_OPTION_ID_PREFIX}${i}`
                                                : undefined
                                        }
                                        onClick={() => applySuggestion(sg.text)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            width: "100%",
                                            textAlign: "left",
                                            padding: "5px 16px",
                                            border: "none",
                                            background:
                                                i === suggestHighlightedIndex ? "#f0f4f8" : "none",
                                            cursor: "pointer",
                                            fontSize: 13,
                                            color: "#3c4650",
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: "#8a94a0",
                                                fontSize: 11,
                                                textTransform: "uppercase",
                                                minWidth: 90,
                                            }}
                                        >
                                            {sg.entity_type.replace(/_/g, " ")}
                                        </span>
                                        <span
                                            style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {sg.text}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* PR-A2: reserved-height QA slot. Renders the moment
                            intent reports isQuestion (skeleton card), and the
                            qa event replaces the skeleton IN PLACE — the fixed
                            inline height guarantees the swap cannot move the
                            result list below (the T4 y-position assertion).
                            A2-review 1511890: an answerless stream end swaps
                            in the quiet no-answer state at the SAME height —
                            the slot never collapses mid-search. A2-review
                            1511891: long answers clamp with a user-initiated
                            expand (height → minHeight) instead of nesting a
                            scroll region. */}
                        {/* graft/A7 v2: for an overview-shaped query the shared
                            slot component renders the AI Overview in the reserved
                            frame (armed before any request; streamed at a stopping
                            point); a lookup renders nothing here. The legacy inline
                            block below is unchanged for v1 builders and when the
                            server has no Rapid QA. */}
                        {rapidActive && (
                            <AiSearchQASlot
                                qaAnswers={state.qaAnswers}
                                phase={state.phase}
                                isQuestion={state.isQuestion}
                                hasQuery={hasQuery}
                                searchId={state.searchId}
                                rapid={rapid.state}
                                rapidArmed={rapid.armed}
                                onOpenSource={openRapidSource}
                                onAgentOpen={handleHideSearchInput}
                            />
                        )}
                        {!rapidActive && showQASlot && (
                            <div
                                className="ai-search-qa-slot"
                                style={
                                    qaSlotExpanded
                                        ? { minHeight: QA_SLOT_HEIGHT_PX }
                                        : { height: QA_SLOT_HEIGHT_PX, overflow: "hidden" }
                                }
                                data-testid="ai-search-qa-slot"
                            >
                                {qaSlotContent === "answer" && (
                                    <>
                                        <AiSearchQA
                                            qaAnswers={state.qaAnswers}
                                            clamped={qaNeedsClamp && !qaExpanded}
                                        />
                                        {qaNeedsClamp && (
                                            <BTButton
                                                type="tertiary"
                                                onClick={() => setQaExpanded((v) => !v)}
                                                data-testid="ai-search-qa-expand"
                                                className="ai-search-qa-expand"
                                            >
                                                {qaExpanded ? "Show less" : "Show full answer"}
                                            </BTButton>
                                        )}
                                    </>
                                )}
                                {qaSlotContent === "skeleton" && <QASkeletonCard />}
                                {qaSlotContent === "no-answer" && (
                                    <div
                                        className="ai-search-qa"
                                        data-testid="ai-search-qa-no-answer"
                                    >
                                        <div className="ai-search-qa-card">
                                            <div className="ai-search-qa-label">AI Answer</div>
                                            <div className="ai-search-qa-text ai-search-qa-text--muted">
                                                No answer available for this question &mdash; the
                                                results below may help.
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* PR-A2: screen-reader-only result-count announcement.
                            Announces once per commit (flag-on) / once per paint
                            (flag-off) — the count header below is no longer a
                            live region, so per-event text churn stays silent.
                            Tailwind's sr-only (review 1511893). */}
                        <span
                            className="sr-only"
                            aria-live="polite"
                            aria-atomic="true"
                            data-testid="ai-search-announcement"
                        >
                            {announcement}
                        </span>

                        {/* Result count header. PR-A2: NOT a live region —
                            the sr-only announcement span above owns the
                            screen-reader count (once per commit/paint);
                            A3's testid kept for the click-telemetry suite. */}
                        {showResults && (
                            <div
                                className="ai-search-count-header"
                                data-testid="ai-search-count-header"
                            >
                                <span>
                                    {state.phase === "streaming" && (
                                        <BTIconLoadingOutlined
                                            spin
                                            size={12}
                                            className="ai-search-streaming-spinner"
                                        />
                                    )}
                                    {state.phase === "streaming"
                                        ? `${resultCountText} so far...`
                                        : resultCountText}
                                </span>
                                <BTButton
                                    type="tertiary"
                                    onClick={() => setIsFeedbackOpen(true)}
                                    data-testid="ai-search-feedback-link"
                                    className="ai-search-feedback-link"
                                >
                                    Not finding it? Tell us
                                </BTButton>
                            </div>
                        )}

                        {isFeedbackOpen && (
                            <FeedbackWidget
                                feedbackTarget={FeedbackTarget.AiSearch}
                                onClose={() => setIsFeedbackOpen(false)}
                                extraInfo={feedbackExtraInfo}
                            />
                        )}

                        {/* Scrollable results. PR-A1: late-op hits render BELOW the
                            committed rows inside the SAME listbox (an aria "group"
                            separator, not a second listbox) so aria-activedescendant
                            containment holds for appended rows — review 1511768. */}
                        {showResults && (
                            <div className="ai-search-results-scroll" ref={resultsRef}>
                                <AiSearchResults
                                    results={results}
                                    appendedResults={showAppended ? appendedResults : undefined}
                                    query={query}
                                    highlightedIndex={highlightedIndex}
                                    pendingKey={pendingKey}
                                    onResultClick={handleResultClick}
                                    builderInfo={builderInfo}
                                />
                            </div>
                        )}

                        {/* PR-A2: per-op errors demoted to a dismissible
                            footer note — some sources didn't respond, but the
                            results that did are on screen and undisturbed. */}
                        {showOpErrorsNote && (
                            <div className="ai-search-op-errors" data-testid="ai-search-op-errors">
                                <span className="ai-search-op-errors-text">
                                    Some sources didn&rsquo;t respond &mdash;{" "}
                                    {state.opErrors.join(" · ")}
                                </span>
                                <BTButton
                                    type="tertiary"
                                    onClick={() => setOpErrorsDismissed(true)}
                                    data-testid="ai-search-op-errors-dismiss"
                                    className="ai-search-op-errors-dismiss"
                                    aria-label="Dismiss source errors"
                                >
                                    Dismiss
                                </BTButton>
                            </div>
                        )}

                        {/* Empty state */}
                        {showEmpty && (
                            <div
                                className="ai-search-empty"
                                aria-live="polite"
                                data-testid="ai-search-empty"
                            >
                                <div className="ai-search-empty-icon" aria-hidden="true">
                                    <BTIconMagnifyingGlassX size={32} />
                                </div>
                                <BTText
                                    size="distinct-sm"
                                    tag="p"
                                    className="ai-search-empty-title"
                                >
                                    No results for &ldquo;{query}&rdquo;
                                </BTText>
                                <BTText size="normal-sm" tag="p" className="ai-search-empty-hint">
                                    Check your spelling or try broader terms.
                                </BTText>
                            </div>
                        )}

                        {/* Footer with keyboard hints */}
                        {showResults && (
                            <div className="ai-search-footer" data-testid="ai-search-footer">
                                <div className="ai-search-shortcuts">
                                    <span className="ai-search-shortcut-group">
                                        <kbd>&uarr;</kbd>
                                        <kbd>&darr;</kbd>
                                        navigate
                                    </span>
                                    <span className="ai-search-shortcut-group">
                                        <kbd>&crarr;</kbd>
                                        select
                                    </span>
                                    <span className="ai-search-shortcut-group">
                                        <kbd>esc</kbd>
                                        close
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
            {/* SearchBarRoutes must stay mounted after the panel closes so the
                pushed /SearchResult/* route can render the entity modal */}
            <Route render={(routeProps) => <SearchBarRoutes {...routeProps} />} />
        </div>
    );
}
